import React from 'react';

/**
 * Live offer preview that sits beside the campaign builder.
 *
 * The hero is a 1:1 replica of the mobile spotlight offer card the customer
 * actually sees (SPOTLIGHT + ends-in pills, big discount, title, dashed
 * divider, coupon + meta) — so the merchant builds against the real artefact,
 * not an abstraction. Below it, a short plain-language explainer spells out
 * who it applies to, the limits, and the steps left to publish.
 *
 * Read-only: derives everything from `config` (assembled by CampaignBuilder).
 */

const SAMPLE_BILL = 1000; // ₹ used for the worked example

const rupee = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// "In 5 days" / "Ends tomorrow" / "In 4 hours" — null when no/elapsed expiry.
function endsInLabel(validUntil) {
  if (!validUntil) return null;
  const end = new Date(String(validUntil).includes('T') ? validUntil : String(validUntil).replace(' ', 'T'));
  if (Number.isNaN(end.getTime())) return null;
  const diff = end.getTime() - Date.now();
  if (diff <= 0) return null;
  if (diff < MS_PER_HOUR) {
    const m = Math.max(1, Math.round(diff / MS_PER_MINUTE));
    return `In ${m} ${m === 1 ? 'minute' : 'minutes'}`;
  }
  if (diff < MS_PER_DAY) {
    const h = Math.round(diff / MS_PER_HOUR);
    return `In ${h} ${h === 1 ? 'hour' : 'hours'}`;
  }
  const d = Math.round(diff / MS_PER_DAY);
  if (d === 1) return 'Ends tomorrow';
  return `In ${d} days`;
}

// Big discount figure for the card: { head, unit, applies }. Null when no
// discount is configured yet.
function cardDiscount(c) {
  const a = c.advanced || {};
  if (a.mechanicType === 'TIERED' && (a.tiers || []).length) {
    const vals = (a.tiers || [])
      .filter((t) => String(t.value ?? '') !== '')
      .map((t) => ({ type: t.type || 'PERCENT', value: num(t.value) }));
    const best = vals.reduce((m, t) => (t.value > m.value ? t : m), { value: 0, type: 'PERCENT' });
    if (best.value > 0) {
      return { head: best.type === 'FLAT' ? rupee(best.value) : `${best.value}%`, unit: 'UP TO', applies: 'Spend more, save more' };
    }
    return { head: 'Tiered', unit: null, applies: 'Spend more, save more' };
  }
  if (a.mechanicType === 'FLAT_PRICE' && String(a.flatPriceValue ?? '') !== '') {
    return { head: rupee(num(a.flatPriceValue)), unit: 'NOW', applies: null };
  }
  if (a.mechanicType === 'FREE_ITEM' && String(a.freeItemLabel || '').trim()) {
    return { head: 'FREE', unit: null, applies: a.freeItemLabel };
  }
  switch (c.discountCategory) {
    case 'PERCENT':
      return num(c.percentOff) ? { head: `${num(c.percentOff)}%`, unit: 'OFF', applies: null } : null;
    case 'FLAT':
      return num(c.flatOff) ? { head: rupee(num(c.flatOff)), unit: 'OFF', applies: null } : null;
    case 'BUY_GET':
      return num(c.buyQty) && num(c.getQty)
        ? { head: `B${num(c.buyQty)}G${num(c.getQty)}`, unit: 'FREE', applies: null }
        : null;
    case 'BILL_VALUE':
      return num(c.spendAmount) && num(c.getAmount)
        ? { head: rupee(num(c.getAmount)), unit: 'OFF', applies: `On ${rupee(c.spendAmount)}+ bills` }
        : null;
    default:
      return null;
  }
}

const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Friendly valid-days label so the card never shows a cramped 7-day list:
// all days → "Every day", Mon–Fri → "Weekdays", Sat/Sun → "Weekends",
// otherwise a canonical-order list. Null when no days are set.
function formatValidDays(label) {
  const set = new Set(String(label || '').split(',').map((s) => s.trim()).filter(Boolean));
  if (set.size === 0) return null;
  if (DAY_ORDER.every((d) => set.has(d))) return 'Every day';
  if (set.size === 5 && ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].every((d) => set.has(d))) return 'Weekdays';
  if (set.size === 2 && set.has('Sat') && set.has('Sun')) return 'Weekends';
  return DAY_ORDER.filter((d) => set.has(d)).join(', ');
}

// Tier rows for the "spend more, save more" (TIERED) mechanic, sorted by spend,
// rendered as bullets on the card. Empty for every other mechanic.
function tierLines(c) {
  const a = c.advanced || {};
  if (a.mechanicType !== 'TIERED') return [];
  return (a.tiers || [])
    .filter((t) => String(t.minSpend ?? '') !== '' && String(t.value ?? '') !== '')
    .map((t) => ({ min: num(t.minSpend), type: t.type || 'PERCENT', value: num(t.value) }))
    .sort((x, y) => x.min - y.min)
    .map((t) => `Spend ${rupee(t.min)} → ${t.type === 'FLAT' ? `${rupee(t.value)} off` : `${t.value}% off`}`);
}

// Short "applies to" line shown beside the discount unit.
function appliesToLabel(c) {
  if (c.appliesOn === 'CATEGORIES' && String(c.appliesCategories || '').trim()) {
    const names = String(c.appliesCategories).split(',').map((s) => s.trim()).filter(Boolean);
    if (names.length === 1) return `On ${names[0]}`;
    return `On ${names.length} services`;
  }
  if (c.appliesOn === 'MIN_CART' && num(c.minCartValue) > 0) {
    return `On ${rupee(c.minCartValue)}+ bills`;
  }
  return 'On all services';
}

// Returns { headline, customerGets, example } describing the discount mechanic.
function describeDiscount(config) {
  const a = config.advanced || {};
  const cap = num(config.maxDiscountCap);
  const capNote = cap > 0 ? ` (up to ${rupee(cap)})` : '';

  if (a.mechanicType === 'TIERED' && (a.tiers || []).length) {
    const tiers = (a.tiers || [])
      .filter((t) => t.minSpend !== '' && t.value !== '')
      .map((t) => ({ min: num(t.min ?? t.minSpend), type: t.type || 'PERCENT', value: num(t.value) }))
      .sort((x, y) => x.min - y.min);
    const best = [...tiers].reverse().find((t) => SAMPLE_BILL >= t.min);
    const lines = tiers.map((t) => `spend ${rupee(t.min)} → ${t.type === 'FLAT' ? rupee(t.value) : `${t.value}%`} off`);
    let example = 'Add tiers to see the effect.';
    if (best) {
      const d = best.type === 'FLAT' ? best.value : Math.round((SAMPLE_BILL * best.value) / 100);
      example = `On a ${rupee(SAMPLE_BILL)} booking → ${rupee(best.type === 'FLAT' ? best.value : d)} off, customer pays ${rupee(SAMPLE_BILL - d)}.`;
    }
    return { headline: 'Tiered discount (spend more, save more)', customerGets: lines.join(' · ') || 'Add at least one tier.', example };
  }
  if (a.mechanicType === 'FLAT_PRICE' && a.flatPriceValue !== '' && a.flatPriceValue != null) {
    const price = num(a.flatPriceValue);
    return {
      headline: 'Flat price',
      customerGets: `The price becomes ${rupee(price)} (instead of the normal price).`,
      example: `On a ${rupee(SAMPLE_BILL)} service → customer pays ${rupee(price)} (saves ${rupee(Math.max(0, SAMPLE_BILL - price))}).`,
    };
  }
  if (a.mechanicType === 'FREE_ITEM' && String(a.freeItemLabel || '').trim()) {
    return {
      headline: 'Free add-on',
      customerGets: `Customers get "${a.freeItemLabel}" free with their booking.`,
      example: `On any qualifying booking → "${a.freeItemLabel}" is added free.`,
    };
  }

  switch (config.discountCategory) {
    case 'PERCENT': {
      const p = num(config.percentOff);
      if (!p) return { headline: 'Percentage off', customerGets: 'Enter a percentage.', example: '' };
      let d = Math.round((SAMPLE_BILL * p) / 100);
      if (cap > 0) d = Math.min(d, cap);
      return {
        headline: `${p}% off${capNote}`,
        customerGets: `Customers get ${p}% off their bill${capNote}.`,
        example: `On a ${rupee(SAMPLE_BILL)} booking → ${rupee(d)} off, customer pays ${rupee(SAMPLE_BILL - d)}.`,
      };
    }
    case 'FLAT': {
      const f = num(config.flatOff);
      if (!f) return { headline: 'Flat amount off', customerGets: 'Enter an amount.', example: '' };
      const d = Math.min(f, SAMPLE_BILL);
      return {
        headline: `${rupee(f)} off`,
        customerGets: `Customers get ${rupee(f)} off their bill.`,
        example: `On a ${rupee(SAMPLE_BILL)} booking → ${rupee(d)} off, customer pays ${rupee(SAMPLE_BILL - d)}.`,
      };
    }
    case 'BUY_GET': {
      const x = num(config.buyQty);
      const y = num(config.getQty);
      if (!x || !y) return { headline: 'Buy X, get Y free', customerGets: 'Enter the buy/get quantities.', example: '' };
      return {
        headline: `Buy ${x}, get ${y} free`,
        customerGets: `For every ${x + y} items booked, the ${y} cheapest are free.`,
        example: `Book ${x + y} services → the cheapest ${y} cost nothing.`,
      };
    }
    case 'BILL_VALUE': {
      const spend = num(config.spendAmount);
      const get = num(config.getAmount);
      if (!spend || !get) return { headline: 'Spend & save', customerGets: 'Enter the spend and reward amounts.', example: '' };
      return {
        headline: `Spend ${rupee(spend)}, get ${rupee(get)} off`,
        customerGets: `Customers who spend at least ${rupee(spend)} get ${rupee(get)} off.`,
        example: `On a ${rupee(spend)}+ booking → ${rupee(get)} off.`,
      };
    }
    default:
      return { headline: 'No discount yet', customerGets: 'Pick a discount above to get started.', example: '' };
  }
}

function buildConditions(config) {
  const a = config.advanced || {};
  const out = [];
  if (config.appliesOn === 'CATEGORIES' && String(config.appliesCategories || '').trim()) {
    out.push(`Only on these services: ${config.appliesCategories}.`);
  } else if (config.appliesOn === 'MIN_CART' && num(config.minCartValue) > 0) {
    out.push(`Only on bills of ${rupee(config.minCartValue)} or more.`);
  } else {
    out.push('Works on any service (store-wide).');
  }
  const audience = String(config.audience || 'all').toLowerCase();
  if (audience === 'new') out.push('Only for first-time customers.');
  else if (audience === 'repeat') out.push('Only for returning customers.');
  else if (audience === 'vip') out.push('Only for VIP / high-spending customers.');
  if (String(a.membershipTier || '').trim()) out.push(`Only for "${a.membershipTier}" members.`);
  if (a.requiresReferral) out.push('Only when the customer was referred.');
  if (String(config.validDaysLabel || '').trim()) out.push(`Only on: ${config.validDaysLabel}.`);
  (a.timeWindows || [])
    .filter((w) => w.start && w.end && (w.days || []).length)
    .forEach((w) => out.push(`Happy hour: ${w.days.join(', ')} ${w.start}–${w.end} (by appointment time).`));
  if (num(a.leadTimeMaxHours) > 0) out.push(`Last-minute only: booked within ${a.leadTimeMaxHours}h of the appointment.`);
  if (num(a.leadTimeMinHours) > 0) out.push(`Early-bird only: booked at least ${a.leadTimeMinHours}h ahead.`);
  return out;
}

function howApplied(config) {
  const a = config.advanced || {};
  if ((a.redemptionMode || 'CODE') === 'AUTO') {
    return 'Applies automatically at checkout — the customer does nothing.';
  }
  const code = String(config.couponCode || '').trim();
  return code
    ? `Customer enters the code "${code}" at checkout.`
    : 'Customer enters a coupon code at checkout (one will be generated).';
}

function buildLimits(config) {
  const a = config.advanced || {};
  const out = [];
  const lt = String(config.redemptionLimitType || 'none');
  const lv = num(config.redemptionLimitValue);
  if (lt === 'per_user' && lv) out.push(`Each customer can use it ${lv === 1 ? 'once' : `${lv} times`}.`);
  else if (lt === 'global' && lv) out.push(`Can be used ${lv} times in total, then it stops.`);
  if (num(a.budgetCeiling) > 0) out.push(`Stops once ${rupee(a.budgetCeiling)} of total discount has been given.`);
  if (num(config.maxDiscountCap) > 0) out.push(`No single customer saves more than ${rupee(config.maxDiscountCap)}.`);
  if (out.length === 0) out.push('No usage limits — unlimited redemptions.');
  return out;
}

const SparkleIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3l1.8 4.9L18.7 9l-4.9 1.8L12 15.7 10.2 10.8 5.3 9l4.9-1.1L12 3z" fill="currentColor" />
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// 1:1 web replica of the mobile spotlight offer card.
function SpotlightPreviewCard({ config }) {
  const c = config || {};
  const discount = cardDiscount(c);
  const applies = discount && discount.applies ? discount.applies : appliesToLabel(c);
  const endsIn = endsInLabel(c.validityUntil);
  const title = String(c.title || '').trim() || 'Your campaign title';
  const subtitle = describeDiscount(c).customerGets;
  const code = String(c.couponCode || '').trim();
  const cap = num(c.maxDiscountCap) > 0 ? `up to ${rupee(c.maxDiscountCap)}` : null;
  const tiers = tierLines(c);
  const minMeta = [
    c.appliesOn === 'MIN_CART' && num(c.minCartValue) > 0 ? `Min ${rupee(c.minCartValue)}` : null,
    formatValidDays(c.validDaysLabel),
  ].filter(Boolean).join(' · ');
  const pillLabel = 'OFFER'; // CP-111 (S3): placement is computed, not chosen

  return (
    <div className="spotlight-card">
      <div className="spotlight-card__decor" aria-hidden="true" />

      <div className="spotlight-card__top">
        <span className="spotlight-card__pill">
          <SparkleIcon />
          {pillLabel}
        </span>
        {endsIn ? <span className="spotlight-card__ends">{endsIn}</span> : null}
      </div>

      {discount ? (
        <div className="spotlight-card__discount">
          <span className="spotlight-card__head">{discount.head}</span>
          <span className="spotlight-card__unitcol">
            {discount.unit ? <span className="spotlight-card__unit">{discount.unit}</span> : null}
            {applies ? <span className="spotlight-card__applies">{applies}</span> : null}
          </span>
        </div>
      ) : (
        <div className="spotlight-card__discount spotlight-card__discount--empty">
          <span className="spotlight-card__head">—</span>
          <span className="spotlight-card__unitcol">
            <span className="spotlight-card__applies">Pick a discount to preview</span>
          </span>
        </div>
      )}

      <div className="spotlight-card__title">{title}</div>
      {tiers.length > 0 ? (
        <ul className="spotlight-card__tiers">
          {tiers.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
      ) : (
        <div className="spotlight-card__subtitle">{subtitle}</div>
      )}

      <div className="spotlight-card__divider" />

      <div className="spotlight-card__coupon-row">
        {code ? (
          <span className="spotlight-card__coupon">
            <CopyIcon />
            {code}
          </span>
        ) : (
          <span className="spotlight-card__coupon spotlight-card__coupon--empty">CODE</span>
        )}
        {(cap || minMeta) ? (
          <span className="spotlight-card__meta">
            {cap ? <span className="spotlight-card__cap">{cap}</span> : null}
            {minMeta ? <span className="spotlight-card__metasub">{minMeta}</span> : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function OfferLivePreview({ config, layout = 'stacked' }) {
  const c = config || {};
  const conditions = buildConditions(c);
  const limits = buildLimits(c);

  const detailBlocks = (
    <>
      <section className="offer-preview__block">
        <h5>To redeem, the customer…</h5>
        <ul>
          <li>{howApplied(c)}</li>
          {conditions.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
      </section>

      <section className="offer-preview__block">
        <h5>Limits &amp; safety</h5>
        <ul>
          {limits.map((line, i) => <li key={i}>{line}</li>)}
          <li>{(c.advanced || {}).stackingMode === 'STACKABLE'
            ? 'Can be combined with other eligible offers.'
            : 'Can’t be combined with other offers (only the best one applies).'}</li>
        </ul>
      </section>
    </>
  );

  // Split layout: the customer card on the left, the redeem/limits detailing on the
  // right. Used by the offer preview modal; the builder keeps the stacked layout.
  if (layout === 'split') {
    return (
      <aside className="offers-live-preview offers-live-preview--split" aria-label="Live offer preview">
        <div className="offer-preview__cardcol">
          <div className="offer-preview__eyebrow">Customer sees</div>
          <SpotlightPreviewCard config={c} />
        </div>
        <div className="offer-preview__detailcol">
          {detailBlocks}
        </div>
      </aside>
    );
  }

  return (
    <aside className="offers-live-preview" aria-label="Live offer preview">
      <div className="offer-preview__eyebrow">Customer sees</div>

      <SpotlightPreviewCard config={c} />

      {detailBlocks}
    </aside>
  );
}
